import { ColDef, SuppressKeyboardEventParams } from '@ag-grid-community/core';
import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { HeaderRendererComponent } from '@frk/eds-components';
import {
  DOCUMENT_LISTING,
  FUND_DOCUMENT_LISTING,
  MY_LITERATURE,
} from '@literature/const/literature-data-const';
import { LiteratureDocumentListingComponent } from '@literature/literature-document-listing/literature-document-listing.component';
import { LiteratureFundDocumentComponent } from '@literature/literature-fund-document/literature-fund-document.component';
import {
  detailsGenerator,
  fundNameGenerator,
  nameGenerator,
  documentNameGenerator,
} from '@literature/literature-utils';
import { MyLiteratureComponent } from '@literature/my-literature/my-literature.component';
import { GridProperties } from '@literature/types';
import { TranslateService } from '@shared/translate/translate.service';
import { createDateComparator, literatureSort } from '@utils/sort-utils';

@Injectable({
  providedIn: 'root',
})
export class LiteratureAgGridHelperService {
  constructor(
    public dateFormatter: DatePipe,
    private translateService: TranslateService
  ) {}

  private isLargeTabletAndSmallDesk: boolean;

  private static suppressDefaultTabbing(
    params: SuppressKeyboardEventParams
  ): boolean {
    const pressedKey = params.event;
    if (pressedKey.code === 'Tab' || pressedKey.key === 'Tab') {
      const buttonsInCell = (pressedKey.target as Element)
        .closest('.ag-cell')
        .querySelectorAll('button, [href]');
      // cond 1 : if no buttons, then default navigation to next cell
      // cond 2 : if already at the last button in cell then default navigation to next cell
      // cond 3 : if at first button and we press Shift + tab default navigation to previous cell.
      if (
        buttonsInCell.length === 0 ||
        (pressedKey.shiftKey === false &&
          pressedKey.target === buttonsInCell[buttonsInCell.length - 1]) ||
        (pressedKey.shiftKey === true && pressedKey.target === buttonsInCell[0])
      ) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }

  // Created Custom multi checkbox for Document Listing.
  getListingMultiSelectCheckBoxColumn(
    reference: LiteratureDocumentListingComponent,
    isColumnLayout?: boolean,
    cellClass: string = 'ag-cell-frk--middle'
  ): ColDef {
    const column: ColDef = {
      headerName: '',
      colId: 'MULTI_SELECT_CHECKBOX',
      headerComponent: 'selectAllGroupHeaderComponent',
      headerComponentParams: {
        reference,
        checkboxLabel: this.translateService.instant(
          'literature.select-all-group-header-input-label'
        ),
      },
      maxWidth: 40,
      cellClass: [cellClass],
      cellRenderer: 'selectCellRendererComponent',
      cellRendererParams: { reference },
    };
    return column;
  }

  getMultiSelectCheckBoxColumn(
    isColumnLayout?: boolean,
    cellClass: string = 'ag-selection-checkbox--middle'
  ): ColDef {
    const column: ColDef = {
      headerName: '',
      colId: 'MULTI_SELECT_CHECKBOX',
      checkboxSelection: true,
      headerCheckboxSelection: isColumnLayout ? false : true,
      // to select the filtered data only
      headerCheckboxSelectionFilteredOnly: true,
      maxWidth: 40,
      cellClass: [cellClass],
      // Intentionally, marked as 'any' since we don't want to return anything.
      // And, void isn't available as the method pre-defined return type.
      // Ref.: https://github.com/ag-grid/ag-grid/issues/1128#issuecomment-953708071
      cellRenderer: (params): any => {
        if (params.data.literatureData.isSelected) {
          params.node.setSelected(true);
        }
      },
    };
    return column;
  }

  getThumbnailImageColumn(isInternational: boolean, componentName?: string) {
    const gridProperties: GridProperties = {};
    if (componentName === FUND_DOCUMENT_LISTING) {
      gridProperties.autoHeight = true;
    } else {
      gridProperties.autoHeight = false;
    }
    const column = {
      headerName: '',
      colId: 'THUMBNAIL_IMAGE',
      cellRenderer: 'iconColumnComponent',
      cellRendererParams: {
        isSiteIntl: isInternational,
      },
      cellClass: ['ag-cell-frk--middle'],
      width: 72,
      autoHeight: gridProperties.autoHeight,
    };
    return column;
  }

  getPdfIconColumn(
    isInternational,
    showOnlyIcon?: boolean,
    isColumnLayout?: boolean
  ) {
    const column = {
      headerName: '',
      colId: 'PDF_ICON',
      cellRenderer: 'iconColumnComponent',
      cellRendererParams: {
        isSiteIntl: isInternational,
        showOnlyIcon,
      },
      maxWidth: isColumnLayout ? 50 : 72,
      cellClass: 'ag-cell-frk--middle',
    };
    return column;
  }

  getActionColumn(
    reference: any,
    componentName: string,
    isInternational?: boolean,
    actionPerformForCart?: string,
    isColumnLayout?: boolean
  ) {
    const gridProperties: GridProperties = {};
    if (componentName === DOCUMENT_LISTING) {
      gridProperties.maxWidth = 440;
      gridProperties.flex = isInternational ? 4 : 3;
      gridProperties.cellClass = [
        'ag-cell-frk--highlightedPrimary',
        'ag-cell-frk--middle',
      ];
      gridProperties.autoHeight = false;
    } else if (componentName === FUND_DOCUMENT_LISTING) {
      gridProperties.flex = isInternational ? 1 : 2;
      gridProperties.cellClass = ['ag-cell-frk--middle', 'ag-cell-frk-padding'];
      gridProperties.autoHeight = true;
    } else {
      gridProperties.maxWidth = 700;
      gridProperties.flex = 5;
      gridProperties.cellClass = [
        'ag-cell-frk--highlightedPrimary',
        'ag-cell-frk--middle',
      ];
      gridProperties.autoHeight = false;
    }
    const column = {
      headerName: isColumnLayout
        ? ''
        : this.translateService.instant('literature.lit-listing-column-action'),
      colId: 'ACTION',
      // NGC-11103 to align header name with column button
      headerClass: 'ag-header-cell-frk--sm',
      cellClass: gridProperties?.cellClass,
      cellRenderer: 'downloadRenderer',
      cellRendererParams: {
        action: reference,
        isSiteIntl: isInternational,
        actionPerformForCart,
        isColumnLayout,
        componentName,
      },
      maxWidth: gridProperties?.maxWidth,
      flex: gridProperties?.flex,
      autoHeight: gridProperties.autoHeight,
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return LiteratureAgGridHelperService.suppressDefaultTabbing(params);
      },
    };
    return column;
  }

  getPublicationDateColumn(
    convertDateTo: string,
    apiDateFormat: string,
    componentName: string,
    isInternational?: boolean
  ) {
    const gridProperties: GridProperties = {};
    if (componentName === DOCUMENT_LISTING) {
      gridProperties.minWidth = 120;
      // if screen size is large tablet or small desktop than min width will be reduced by 50px
      if (this.isLargeTabletAndSmallDesk) {
        gridProperties.minWidth = gridProperties.minWidth - 50;
      }
      gridProperties.maxWidth = 150;
      gridProperties.autoHeight = false;
      gridProperties.cellClass = [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--middle',
      ];
      gridProperties.sortable = true;
      gridProperties.sortingOrder = ['desc', 'asc'];
    } else if (componentName === FUND_DOCUMENT_LISTING) {
      gridProperties.maxWidth = isInternational ? 200 : 105;
      gridProperties.cellClass = ['ag-cell-frk--middle'];
      gridProperties.autoHeight = true;
      gridProperties.sortable = true;
      gridProperties.sortingOrder = ['desc', 'asc'];
    } else {
      gridProperties.minWidth = 120;
      gridProperties.maxWidth = 200;
      gridProperties.cellClass = [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--middle',
      ];
      gridProperties.autoHeight = false;
      gridProperties.sortable = false;
      gridProperties.sortingOrder = [null];
    }

    const column = {
      headerName: this.translateService.instant(
        'literature.lit-listing-column-publicationdate'
      ),
      colId: 'PUBLICATION_DATE',
      valueGetter: (params) => {
        return `${params.data.literatureData.frkReferenceDate}`;
      },
      valueFormatter: (params) => {
        if (params.value !== 'undefined') {
          return this.dateFormatter.transform(params.value, convertDateTo);
        }
        return '-';
      },
      minWidth: gridProperties?.minWidth,
      maxWidth: gridProperties?.maxWidth,
      flex: 1,
      comparator: createDateComparator(apiDateFormat),
      sortable: gridProperties.sortable,
      sortingOrder: gridProperties.sortingOrder,
      hide: false,
      cellClass: gridProperties?.cellClass,
      autoHeight: gridProperties?.autoHeight,
      headerComponentFramework: HeaderRendererComponent,
    };
    return column;
  }

  getDetailColumn(
    isInternational: boolean,
    componentName: string,
    isInvestor?: boolean,
    isColumnLayout?: boolean,
    isLuxembourg?: boolean
  ) {
    const gridProperties: GridProperties = {};
    if (componentName === DOCUMENT_LISTING) {
      gridProperties.cellClass = [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--middle',
      ];
      gridProperties.flex = 2;
      gridProperties.autoHeight = false;
      const nonLuxembourgwidth = isInternational ? 350 : 304;
      gridProperties.minWidth = !isLuxembourg ? nonLuxembourgwidth : 325;

      // if screen size is large tablet or small desktop than min width will be reduced by 180px
      if (this.isLargeTabletAndSmallDesk) {
        gridProperties.minWidth = gridProperties.minWidth - 180;
      }
      gridProperties.cellStyle = {};
    } else if (componentName === FUND_DOCUMENT_LISTING) {
      gridProperties.cellClass = isInternational
        ? ['ag-cell-frk--middle', 'ag-cell-frk-padding']
        : ['ag-cell-frk--highlightedSecondary', 'ag-cell-frk--middle'];
      gridProperties.flex = 2;
      gridProperties.autoHeight = true;
      if (isColumnLayout) {
        gridProperties.minWidth = 150;
      } else {
        gridProperties.minWidth = isInternational ? 350 : 220;
      }
      gridProperties.cellStyle = isColumnLayout
        ? {
            'white-space': 'normal',
            'padding-right': '40px',
          }
        : { 'white-space': 'pre-line' };
      isInvestor = isInvestor;
    } else {
      gridProperties.flex = 2;
      gridProperties.autoHeight = false;
      gridProperties.minWidth = 270;
      gridProperties.cellClass = [
        'ag-cell-frk--highlightedSecondary',
        'ag-cell-frk--middle',
      ];
      gridProperties.cellStyle = {};
    }
    const column = {
      headerName: this.translateService.instant(
        'literature.lit-listing-column-detail'
      ),
      colId: 'DETAIL',
      cellRendererParams: {
        isSiteIntl: isInternational,
        isInvestor,
      },
      sortable: isInternational,
      sortingOrder: ['asc', 'desc'],
      valueGetter: (params) => {
        return detailsGenerator(params.data, this.translateService);
      },
      wrapText: true,
      cellRenderer: 'detailRenderer',
      cellClass: gridProperties?.cellClass,
      flex: gridProperties?.flex,
      autoHeight: gridProperties?.autoHeight,
      minWidth: gridProperties?.minWidth,
      cellStyle: gridProperties?.cellStyle,
      headerComponentFramework: HeaderRendererComponent,
    };
    return column;
  }

  getCustomPdfColumn(
    isInternational: boolean,
    colId: string,
    columnName: string,
    width: number,
    cellClassRequired: boolean,
    isPreviewLink = false
  ) {
    const column = {
      headerName: this.translateService.instant(columnName),
      colId,
      cellRenderer: 'iconColumnComponent',
      cellRendererParams: {
        isSiteIntl: isInternational,
        isPreviewLink,
      },
      cellClass: cellClassRequired ? ['ag-cell-frk--highlightedSecondary'] : [],
      width,
      cellStyle: { 'align-items': 'center', display: 'flex' },
      autoHeight: colId === 'SEMI-ANNUAL-REPORT' ? true : false,
    };
    return column;
  }

  getNameColumn(
    componentName: string,
    isInternational: boolean,
    litDetailUrl: string,
    isUpdateNotificationEnabled: boolean,
    showOnlyDocumentName?: boolean,
    isColumnLayout?: boolean,
    isCanada?: boolean,
    isLuxembourg?: boolean
  ) {
    const gridProperties: GridProperties = {};
    if (componentName === DOCUMENT_LISTING || componentName === MY_LITERATURE) {
      const nonLuxembourgwidth = isInternational ? 350 : 344;
      gridProperties.minWidth = !isLuxembourg ? nonLuxembourgwidth : 325;

      // if screen size is large tablet or small desktop than min width will be reduced by 180px
      if (this.isLargeTabletAndSmallDesk) {
        gridProperties.minWidth = gridProperties.minWidth - 180;
      }
      gridProperties.autoHeight = false;
      gridProperties.wrapText = true;
      gridProperties.cellStyle = {};
      gridProperties.flex = isInternational ? 4 : 3;
      if (componentName === DOCUMENT_LISTING) {
        gridProperties.sortable = true;
        gridProperties.sortingOrder = ['asc', 'desc'];
        gridProperties.sort = 'asc';
      } else {
        gridProperties.sortable = false;
        gridProperties.sortingOrder = [null];
        gridProperties.sort = null;
      }
    } else {
      gridProperties.minWidth = isInternational && !isColumnLayout ? 250 : 0;
      gridProperties.flex = isInternational ? 1 : 2;
      gridProperties.autoHeight = true;
      gridProperties.wrapText = isInternational ? false : true;
      gridProperties.cellStyle = showOnlyDocumentName
        ? { 'white-space': 'normal' }
        : { 'white-space': 'pre-line' };
      if (componentName === FUND_DOCUMENT_LISTING) {
        gridProperties.sortable = false;
        gridProperties.sortingOrder = [null];
        gridProperties.sort = null;
      } else {
        gridProperties.sortable = true;
        gridProperties.sortingOrder = ['asc', 'desc'];
        gridProperties.sort = 'asc';
      }
    }
    const column = {
      headerName: this.translateService.instant(
        'literature.lit-listing-column-name'
      ),
      colId: 'NAME',
      cellClass: isColumnLayout
        ? ['ag-cell-frk--middle']
        : ['ag-cell-frk--highlightedPrimary', 'ag-cell-frk--middle'],
      valueGetter: (params) => {
        if (showOnlyDocumentName) {
          return documentNameGenerator(params.data, this.translateService);
        } else {
          return isInternational
            ? nameGenerator(params.data, this.translateService)
            : fundNameGenerator(params.data, this.translateService);
        }
      },
      cellRenderer: 'fundNameCellRendererComponent',
      cellRendererParams: {
        isSiteIntl: isInternational,
        litDetailUrl,
        isUpdateNotificationEnabled,
        showOnlyDocumentName,
        isColumnLayout,
        isCanada,
      },
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return LiteratureAgGridHelperService.suppressDefaultTabbing(params);
      },
      minWidth: gridProperties?.minWidth,
      sortable: gridProperties.sortable,
      sortingOrder: gridProperties.sortingOrder,
      sort: gridProperties.sort,
      comparator: literatureSort,
      flex: gridProperties?.flex,
      cellStyle: gridProperties.cellStyle,
      wrapText: gridProperties?.wrapText,
      autoHeight: gridProperties?.autoHeight,
      headerComponentFramework: HeaderRendererComponent,
    };
    return column;
  }

  getTitleColumn(
    componentName,
    isInternational,
    litDetailUrl,
    isColumnLayout?: boolean
  ) {
    const gridProperties: GridProperties = {};
    gridProperties.minWidth = isInternational && !isColumnLayout ? 250 : 0;
    gridProperties.flex = isInternational ? 1 : 2;
    gridProperties.autoHeight = true;
    gridProperties.wrapText = isInternational ? false : true;
    gridProperties.cellStyle = {
      'white-space': 'normal',
      'padding-right': '40px',
      left: '40px',
    };
    gridProperties.sortable = false;
    gridProperties.sortingOrder = [null];
    gridProperties.sort = null;
    const column = {
      headerName: this.translateService.instant(
        'literature.lit-listing-column-name'
      ),
      colId: 'TITLE',
      cellClass: isColumnLayout
        ? ['ag-cell-frk--middle', 'ag-cell-frk-padding']
        : ['ag-cell-frk--highlightedPrimary', 'ag-cell-frk--middle'],
      valueGetter: (params) => {
        return documentNameGenerator(params.data, this.translateService);
      },
      cellRenderer: 'fundNameCellRendererComponent',
      cellRendererParams: {
        isSiteIntl: isInternational,
        isColumnLayout,
        showOnlyTitle: true,
        showOnlyDocumentName: true,
        litDetailUrl,
        componentName,
      },
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return LiteratureAgGridHelperService.suppressDefaultTabbing(params);
      },
      minWidth: gridProperties?.minWidth,
      sortable: gridProperties.sortable,
      sortingOrder: gridProperties.sortingOrder,
      sort: gridProperties.sort,
      comparator: literatureSort,
      flex: gridProperties?.flex,
      cellStyle: gridProperties.cellStyle,
      wrapText: gridProperties?.wrapText,
      autoHeight: gridProperties?.autoHeight,
      headerComponentFramework: HeaderRendererComponent,
    };
    return column;
  }

  getDocumentListingColDef(
    isInternational: boolean,
    isInvestor: boolean,
    litDetailUrl: string,
    apiDateFormat: string,
    reference: LiteratureDocumentListingComponent,
    convertDateTo: string,
    isCanada: boolean,
    isLuxembourg: boolean,
    isLargeTabletAndSmallDesk?: boolean
  ): ColDef[] {
    // this will set local variable to true as and when user will be on large tablet and small desktop resolution
    if (isLargeTabletAndSmallDesk) {
      this.isLargeTabletAndSmallDesk = isLargeTabletAndSmallDesk;
    }
    return [
      this.getListingMultiSelectCheckBoxColumn(reference),
      this.getThumbnailImageColumn(isInternational),
      this.getPdfIconColumn(isInternational),
      this.getNameColumn(
        DOCUMENT_LISTING,
        isInternational,
        litDetailUrl,
        false,
        false,
        false,
        isCanada,
        isLuxembourg
      ),
      this.getDetailColumn(
        isInternational,
        DOCUMENT_LISTING,
        isInvestor,
        false,
        isLuxembourg
      ),
      this.getPublicationDateColumn(
        convertDateTo,
        apiDateFormat,
        DOCUMENT_LISTING
      ),
      this.getActionColumn(reference, DOCUMENT_LISTING, isInternational),
    ];
  }

  /**
   *
   * @param isInternational indicates if this is international sites
   * @param documentsWithPreview configuration of columns which requires Preview link instead direct download
   */
  getCustomDocumentListingColDef(
    isInternational: boolean,
    documentsWithPreview: string[]
  ): ColDef[] {
    return [
      {
        headerName: this.translateService.instant(
          'literature.lit-listing-column-name'
        ),
        colId: 'NAME',
        valueGetter: (params) => {
          return params.data.product.fundName;
        },
        cellRenderer: 'RegulatoryFundNameCellRenderer',
        width: 187,
        sortable: true,
        sortingOrder: ['asc', 'desc'],
        sort: 'asc',
        cellStyle: { 'align-items': 'center', display: 'flex' },
        flex: 1,
        wrapText: true,
        comparator: literatureSort,
      },
      {
        headerName: this.translateService.instant('literature.SHARE_CLASS'),
        colId: 'SHARE_CLASS',
        valueGetter: (params) => {
          return params.data.shareClass;
        },
        width: 180,
        cellStyle: { 'align-items': 'center', display: 'flex' },
        wrapText: true,
        autoHeight: true,
      },
      this.getCustomPdfColumn(
        isInternational,
        'SUMMARY-PROSPECTUS',
        'literature.SUMMARY-PROSPECTUS',
        80,
        true,
        documentsWithPreview.includes('SUMMARY-PROSPECTUS')
      ),
      this.getCustomPdfColumn(
        isInternational,
        'PROSPECTUS',
        'literature.PROSPECTUS',
        80,
        true,
        documentsWithPreview.includes('PROSPECTUS')
      ),
      this.getCustomPdfColumn(
        isInternational,
        'STATEMENT-OF-ADDITIONAL-INFORMATION',
        'literature.SAI',
        65,
        false,
        documentsWithPreview.includes('STATEMENT-OF-ADDITIONAL-INFORMATION')
      ),
      this.getCustomPdfColumn(
        isInternational,
        'ANNUAL-REPORT',
        'literature.ANNUAL-REPORT',
        65,
        false,
        documentsWithPreview.includes('ANNUAL-REPORT')
      ),
      this.getCustomPdfColumn(
        isInternational,
        'SEMI-ANNUAL-REPORT',
        'literature.SEMI-ANNUAL-REPORTS',
        80,
        false,
        documentsWithPreview.includes('SEMI-ANNUAL-REPORT')
      ),
      this.getCustomPdfColumn(
        isInternational,
        'FISCAL-Q1-HOLDINGS',
        'literature.Q1-HOLDING',
        80,
        true,
        documentsWithPreview.includes('Q1-HOLDING') ||
          documentsWithPreview.includes('FISCAL-Q1-HOLDINGS')
      ),
      this.getCustomPdfColumn(
        isInternational,
        'FISCAL-Q3-HOLDINGS',
        'literature.Q3-HOLDING',
        80,
        true,
        documentsWithPreview.includes('Q3-HOLDING') ||
          documentsWithPreview.includes('FISCAL-Q3-HOLDINGS')
      ),
    ];
  }

  getFundDocumentListingColDef(
    isInternational: boolean,
    litDetailUrl: string,
    apiDateFormat: string,
    reference: LiteratureFundDocumentComponent,
    convertDateTo: string,
    isInvestor: boolean,
    actionPerformForCart?: string,
    isColumnLayout?: boolean
  ) {
    return [
      this.getMultiSelectCheckBoxColumn(
        isColumnLayout,
        'ag-selection-checkbox--middle'
      ),
      this.getThumbnailImageColumn(isInternational, FUND_DOCUMENT_LISTING),
      this.getPdfIconColumn(isInternational, true, isColumnLayout),
      this.getNameColumn(
        FUND_DOCUMENT_LISTING,
        isInternational,
        litDetailUrl,
        false,
        true,
        isColumnLayout
      ),
      this.getTitleColumn(
        FUND_DOCUMENT_LISTING,
        isInternational,
        litDetailUrl,
        isColumnLayout
      ),
      this.getDetailColumn(
        isInternational,
        FUND_DOCUMENT_LISTING,
        isInvestor,
        isColumnLayout
      ),
      this.getPublicationDateColumn(
        convertDateTo,
        apiDateFormat,
        FUND_DOCUMENT_LISTING,
        isInternational
      ),
      this.getActionColumn(
        reference,
        FUND_DOCUMENT_LISTING,
        isInternational,
        actionPerformForCart,
        isColumnLayout
      ),
    ];
  }

  getMyLiteratureColDef(
    isInternational: boolean,
    isInvestor: boolean,
    litDetailUrl: string,
    apiDateFormat: string,
    reference: MyLiteratureComponent,
    actionPerformForCart: string,
    isCanada: boolean
  ): ColDef[] {
    return [
      this.getMultiSelectCheckBoxColumn(),
      this.getThumbnailImageColumn(isInternational),
      this.getNameColumn(
        MY_LITERATURE,
        isInternational,
        litDetailUrl,
        true,
        false,
        false,
        isCanada
      ),
      this.getDetailColumn(isInternational, MY_LITERATURE, isInvestor),
      this.getPublicationDateColumn(
        apiDateFormat,
        apiDateFormat,
        MY_LITERATURE
      ),
      this.getActionColumn(
        reference,
        MY_LITERATURE,
        isInternational,
        actionPerformForCart
      ),
    ];
  }
}
